/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import moment from 'moment-mini';
import './locales/vi'; // Vietnamese locale
import './locales/en-gb'; // British English locale

import '!file-loader?name=[name].[ext]!./images/favicon.ico';

// Import all the third party stuff
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import isPropValid from '@emotion/is-prop-valid';
import { StyleSheetManager } from 'styled-components';
// Needed for redux-saga es6 generator support
import * as Sentry from 'utils/sentryLazy';

// Import root app
import App from 'containers/App/';
import { Router } from 'react-router-dom';
import { ErrorBoundary } from 'components/ErrorBoundary';
import ErrorOverlayManager from 'components/ErrorOverlayManager';
// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';
import { Provider } from 'react-redux';
import configureStore from './configureStore';
import { createReduxHistory } from 'utils/history';
import { message, ConfigProvider } from 'antd';
import release from 'config/release.json';
import { translationMessages } from 'i18n';
import { theme } from 'theme';
import setLanguageInLocalStorage from 'utils/setLanguageAtStart';
import qs from 'qs';
import { enableLogger } from 'logger';
import { setStore } from './store';
import rootSaga from './rootSaga';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';

const search = qs.parse(window.location.search, { ignoreQueryPrefix: true });
setLanguageInLocalStorage(search, window.location.pathname);

message.config({ duration: 6 });
// Static message API does not consume ConfigProvider context — sync theme globally
ConfigProvider.config({ theme: theme });

if (process.env.APP_ENV !== 'development') {
  const initSentry = () =>
    Sentry.init({
      environment: process.env.APP_ENV,
      release: release.version || process.env.RELEASE || 'latest',
      dsn: 'https://40aa3cbb5ec949ef8a8a18a20d5c89ca@sentry.io/1408425',
    });

  const scheduleSentry = () => {
    if (typeof (window as any).requestIdleCallback === 'function') {
      (window as any).requestIdleCallback(initSentry, { timeout: 10000 });
    } else {
      setTimeout(initSentry, 3000);
    }
  };
  if (document.readyState === 'complete') {
    scheduleSentry();
  } else {
    window.addEventListener('load', scheduleSentry, { once: true });
  }
}

// Import i18n messages

moment.locale(window.localStorage.getItem('lang') === 'vi' ? 'vi' : 'en-gb');

// Create redux store with history
const initialState = {};
const store = configureStore(initialState);
const reduxHistory = createReduxHistory(store);
setStore(store);
store.runSaga(rootSaga);
const MOUNT_NODE = document.getElementById('root') as HTMLElement;

function perfNow() {
  if (window.performance) {
    return Math.round(performance.now());
  }

  return 10000;
}

function trackTiming(category, variable, value, label) {
  /** global ga */
  if (window.performance && typeof window.ga === 'function') {
    window.ga('send', {
      hitType: 'timing',
      timingCategory: category,
      timingVar: variable,
      timingLabel: label,
      timingValue: value,
    });
  }
}

enableLogger();
trackTiming('JS', 'load', perfNow(), 'app.tsx');

const render = (messages, Component = App) => {
  trackTiming('JS', 'time_to_render', perfNow(), 'app.tsx');
  ReactDOM.render(
    <>
      {/* sc6 bỏ auto-filter prop lạ trước khi ghi ra DOM (v4 tự lọc). Khôi phục hành vi v4:
          chỉ lọc khi render thẻ DOM (target là string); component tự nhận đủ props. */}
      <StyleSheetManager shouldForwardProp={(prop, target) => (typeof target === 'string' ? isPropValid(prop) : true)}>
        <ErrorBoundary>
          <ConfigProvider theme={theme}>
            <Provider store={store}>
              <LanguageProvider messages={messages}>
                <Router history={reduxHistory}>
                  <Component />
                </Router>
              </LanguageProvider>
            </Provider>
          </ConfigProvider>
        </ErrorBoundary>
      </StyleSheetManager>
      {process.env.APP_ENV !== 'production' ? <ErrorOverlayManager /> : null}
    </>,
    MOUNT_NODE,
    () => {
      trackTiming('JS', 'rendered', perfNow(), 'app.tsx');
    },
  );
};

declare const module: any;
if (module.hot) {
  module.hot.accept(['./i18n', './containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    // tslint:disable-next-line:max-line-length
    const App = require('./containers/App').default; // https://github.com/webpack/webpack-dev-server/issues/100
    render(translationMessages, App);
  });
}
// Chunked polyfill for browsers without Intl support
if (!(window as any).Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en.js'), import('intl/locale-data/jsonp/vi.js')])) // eslint-disable-line prettier/prettier
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

/* GTM is injected directly via a script tag in index.hbs; events are pushed
   straight to window.dataLayer in app/utils/triggerGA4Event.ts */
